import React, { useEffect, useRef, useState } from 'react';
import { Editor, EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Icon from './Icon';

interface RichTextEditorProps {
  /** Initial HTML. The editor is uncontrolled — remount (change `key`) to load new content. */
  initialHtml: string;
  onChange: (html: string) => void;
  /** Called once with the editor's normalised HTML, to use as the "unchanged" baseline. */
  onReady?: (html: string) => void;
  /** Extra classes for the content area (e.g. legal-prose-numbered). */
  contentClassName?: string;
}

/*
 * Turn what an admin types into a usable href: bare domains get https://,
 * bare email addresses get mailto:. Site paths (/privacy-policy), anchors,
 * and http(s)/mailto/tel links are kept as typed.
 */
const normaliseHref = (raw: string): string | null => {
  const v = raw.trim();
  if (!v) return null;
  if (/^(https?:|mailto:|tel:)/i.test(v) || v.startsWith('/') || v.startsWith('#')) return v;
  if (/^[^\s@/]+@[^\s@/]+\.[^\s@/]+$/.test(v)) return `mailto:${v}`;
  if (/^[^\s/]+\.[a-z]{2,}(\/.*)?$/i.test(v)) return `https://${v}`;
  return null;
};

const isExternal = (href: string): boolean => /^https?:/i.test(href);

interface ToolButtonProps {
  icon: string;
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
}

const ToolButton: React.FC<ToolButtonProps> = ({ icon, label, active, disabled, onClick }) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    aria-pressed={active}
    disabled={disabled}
    // Keep the editor's selection when the button is pressed.
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors disabled:opacity-35 disabled:cursor-not-allowed"
    style={{
      background: active ? 'var(--brand-bg)' : 'transparent',
      color: active ? 'var(--brand)' : 'var(--g600)',
    }}
  >
    <Icon name={icon} size={16} />
  </button>
);

const Divider = () => <span className="w-px h-5 bg-g200 mx-1" aria-hidden="true" />;

/** Inline bar for adding, editing or removing a link on the selection. */
const LinkBar: React.FC<{ editor: Editor; onClose: () => void }> = ({ editor, onClose }) => {
  const current = (editor.getAttributes('link').href as string | undefined) || '';
  const [value, setValue] = useState<string>(current);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const apply = () => {
    const href = normaliseHref(value);
    if (!href) {
      setError('Enter a web address, email address, or a site path like /privacy-policy');
      return;
    }
    const attrs = { href, target: isExternal(href) ? '_blank' : null };
    const chain = editor.chain().focus().extendMarkRange('link');
    if (editor.state.selection.empty && !editor.isActive('link')) {
      // Nothing selected: insert the address itself as the link text.
      const text = href.replace(/^mailto:/i, '');
      chain.insertContent({ type: 'text', text, marks: [{ type: 'link', attrs }] }).run();
    } else {
      chain.setLink(attrs).run();
    }
    onClose();
  };

  const remove = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    onClose();
  };

  return (
    <div className="px-3 py-2.5 border-b border-g100 bg-g50">
      <div className="flex items-center gap-2 flex-wrap">
        <Icon name="link" size={15} color="var(--g500)" />
        <input
          ref={inputRef}
          className="form-input flex-1 min-w-[200px] py-1.5 text-[13px]"
          placeholder="https://example.com, name@example.com or /terms-of-service"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              apply();
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              onClose();
              editor.commands.focus();
            }
          }}
        />
        <button type="button" className="btn btn-primary btn-sm" onClick={apply}>
          {current ? 'Update link' : 'Add link'}
        </button>
        {current && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={remove}>
            <Icon name="unlink" size={13} />
            Remove
          </button>
        )}
        <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
          Cancel
        </button>
      </div>
      {error && (
        <div className="text-[12px] mt-1.5" style={{ color: 'var(--red)' }}>
          {error}
        </div>
      )}
    </div>
  );
};

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  initialHtml,
  onChange,
  onReady,
  contentClassName = '',
}) => {
  const [linkOpen, setLinkOpen] = useState<boolean>(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        code: false,
        codeBlock: false,
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: 'https',
        HTMLAttributes: { target: null, rel: null, class: null },
      }),
    ],
    content: initialHtml,
    editorProps: {
      attributes: {
        class: `legal-prose ${contentClassName}`.trim(),
        'aria-label': 'Page content',
      },
    },
    onCreate: ({ editor: e }) => onReady?.(e.getHTML()),
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
  });

  if (!editor) return null;

  const block = editor.isActive('heading', { level: 2 })
    ? 'h2'
    : editor.isActive('heading', { level: 3 })
      ? 'h3'
      : 'p';

  return (
    // overflow-clip (not hidden) so the sticky toolbar still sticks while the page scrolls.
    <div className="rich-editor rounded-xl border border-g200 bg-white overflow-clip focus-within:border-brand transition-colors">
      <div
        className="flex items-center flex-wrap gap-0.5 px-2 py-1.5 border-b border-g100 bg-white sticky top-0 z-10"
        role="toolbar"
        aria-label="Formatting"
      >
        <ToolButton
          icon="pilcrow"
          label="Paragraph"
          active={block === 'p' && !editor.isActive('bulletList') && !editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().setParagraph().run()}
        />
        <ToolButton
          icon="heading-2"
          label="Section heading (numbered, listed in Contents)"
          active={block === 'h2'}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        />
        <ToolButton
          icon="heading-3"
          label="Subheading"
          active={block === 'h3'}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        />
        <Divider />
        <ToolButton
          icon="bold"
          label="Bold (Ctrl+B)"
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        />
        <ToolButton
          icon="italic"
          label="Italic (Ctrl+I)"
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        />
        <ToolButton
          icon="strikethrough"
          label="Strikethrough"
          active={editor.isActive('strike')}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        />
        <Divider />
        <ToolButton
          icon="list"
          label="Bulleted list"
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        />
        <ToolButton
          icon="list-ordered"
          label="Numbered list"
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        />
        <ToolButton
          icon="quote"
          label="Quote"
          active={editor.isActive('blockquote')}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        />
        <ToolButton
          icon="minus"
          label="Divider line"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
        />
        <Divider />
        <ToolButton
          icon="link"
          label="Link"
          active={editor.isActive('link') || linkOpen}
          onClick={() => setLinkOpen((o) => !o)}
        />
        <Divider />
        <ToolButton
          icon="undo"
          label="Undo (Ctrl+Z)"
          disabled={!editor.can().undo()}
          onClick={() => editor.chain().focus().undo().run()}
        />
        <ToolButton
          icon="redo"
          label="Redo (Ctrl+Shift+Z)"
          disabled={!editor.can().redo()}
          onClick={() => editor.chain().focus().redo().run()}
        />
      </div>

      {linkOpen && <LinkBar editor={editor} onClose={() => setLinkOpen(false)} />}

      <EditorContent editor={editor} />
    </div>
  );
};

export default RichTextEditor;
